import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus } from 'lucide-react';
import type { ConversationSummary } from '@nbh/shared';
import { api } from '../lib/api';
import { cacheFirst } from '../lib/cacheFirst';
import { cacheInbox, getCachedInbox } from '../lib/db';
import { useDirectMessages } from '../hooks/useDirectMessages';
import { useNow } from '../hooks/useNow';
import { ChatListRow } from '../components/chat/ChatListRow';
import { PeoplePicker } from '../components/chat/PeoplePicker';
import { MessageThread } from '../components/pwa/MessageThread';

type Open = { id: string; counterpartId: string; name: string } | null;

/**
 * Staff-to-staff messages (SPEC.md §5.3, decision D29).
 *
 * Distinct from the Chat tab on a job, which is a thread *about* that job and belongs
 * to its record. This is a conversation between two people and is nobody else's.
 */
export function MessagesPage() {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<ConversationSummary[] | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One clock for every row's time label, not one per row.
  const now = useNow(60_000);
  const cancelRef = useRef<(() => void) | null>(null);

  // Cache first (Rule 4): the inbox this device last saw is what opens a thread with no
  // signal, and the threads themselves are cached the same way.
  const load = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = cacheFirst<ConversationSummary[]>({
      readCache: getCachedInbox,
      load: api.listConversations,
      writeCache: cacheInbox,
      onValue: (rows) => {
        setConversations(rows);
        setError(null);
      },
      onError: (caught) => setError(caught.message),
    });
  }, []);

  useEffect(() => {
    load();
    return () => cancelRef.current?.();
  }, [load]);

  // A message can start a conversation that is not in this list yet, so the inbox
  // refreshes on any arrival rather than trying to patch a row it may not hold.
  useDirectMessages(load);

  if (open) {
    return (
      <MessageThread
        conversationId={open.id}
        counterpartId={open.counterpartId}
        counterpartName={open.name}
        onBack={() => {
          setOpen(null);
          load();
        }}
      />
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">{t('messages.heading')}</h1>
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="flex min-h-[48px] items-center gap-2 rounded-lg bg-brookfields-green px-4 text-sm font-semibold text-white ring-1 ring-brookfields-gold/70"
        >
          <MessageSquarePlus className="size-4" aria-hidden="true" />
          {t('messages.new')}
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-400">
          {error}
        </p>
      )}

      {conversations === null ? (
        <p className="mt-4 text-sm text-slate-400">{t('messages.loading')}</p>
      ) : conversations.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">{t('messages.empty')}</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-800 rounded-xl border border-slate-800">
          {conversations.map((conversation) => (
            <li key={conversation.id}>
              <ChatListRow
                avatarId={conversation.counterpart.id}
                avatarName={conversation.counterpart.name}
                title={conversation.counterpart.name}
                // A 1:1 preview carries no sender prefix — there is only one other person
                // it could be, and the reference does the same.
                preview={conversation.lastMessage?.body ?? t('messages.noMessagesYet')}
                at={conversation.lastMessage?.createdAt ?? null}
                now={now}
                onOpen={() =>
                  setOpen({
                    id: conversation.id,
                    counterpartId: conversation.counterpart.id,
                    name: conversation.counterpart.name,
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}

      {picking && (
        <PeoplePicker
          onClose={() => setPicking(false)}
          onPicked={(person, conversationId) => {
            setPicking(false);
            setOpen({ id: conversationId, counterpartId: person.id, name: person.name });
          }}
          onError={setError}
        />
      )}
    </div>
  );
}
